import { round } from "./math.js";
import { toGeoJson } from "./serialization.js";
function roundedJson(value, precision) {
    if (typeof value === "number")
        return round(value, precision);
    if (Array.isArray(value)) {
        return value.map((item) => roundedJson(item, precision));
    }
    if (value && typeof value === "object") {
        return Object.fromEntries(Object.entries(value).map(([key, item]) => [
            key,
            roundedJson(item, precision),
        ]));
    }
    return value;
}
export class GeoJsonExporter {
    precision;
    id = "geojson";
    extension = "geojson";
    mimeType = "application/geo+json";
    constructor(precision = 6) {
        this.precision = precision;
    }
    export(scene, options) {
        const contents = JSON.stringify(roundedJson(toGeoJson(scene, options), this.precision), null, 2) + "\n";
        return {
            filename: `${scene.event.id}.geojson`,
            mimeType: this.mimeType,
            contents,
        };
    }
}
function escapeXml(value) {
    return value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&apos;");
}
function coordinateText(coordinates, precision) {
    return coordinates
        .map(([longitude, latitude]) => `${round(longitude, precision)},${round(latitude, precision)},0`)
        .join(" ");
}
function geometryKml(geometry, precision) {
    switch (geometry.type) {
        case "Point":
            return `<Point><coordinates>${coordinateText([geometry.coordinates], precision)}</coordinates></Point>`;
        case "LineString":
            return `<LineString><tessellate>1</tessellate><coordinates>${coordinateText(geometry.coordinates, precision)}</coordinates></LineString>`;
        case "MultiLineString":
            return `<MultiGeometry>${geometry.coordinates
                .map((line) => `<LineString><tessellate>1</tessellate><coordinates>${coordinateText(line, precision)}</coordinates></LineString>`)
                .join("")}</MultiGeometry>`;
        case "Polygon":
            return `<Polygon><tessellate>1</tessellate><outerBoundaryIs><LinearRing><coordinates>${coordinateText(geometry.coordinates[0] ?? [], precision)}</coordinates></LinearRing></outerBoundaryIs></Polygon>`;
        case "MultiPolygon":
            return `<MultiGeometry>${geometry.coordinates
                .map((polygon) => `<Polygon><tessellate>1</tessellate><outerBoundaryIs><LinearRing><coordinates>${coordinateText(polygon[0] ?? [], precision)}</coordinates></LinearRing></outerBoundaryIs></Polygon>`)
                .join("")}</MultiGeometry>`;
    }
}
export class KmlExporter {
    precision;
    id = "kml";
    extension = "kml";
    mimeType = "application/vnd.google-earth.kml+xml";
    constructor(precision = 6) {
        this.precision = precision;
    }
    export(scene, options) {
        const collection = toGeoJson(scene, {
            ...options,
            seam: "unsplit",
        });
        const placemarks = collection.features
            .map((feature) => {
            const name = String(feature.properties.name ??
                feature.id ??
                "Eclipse feature");
            const description = escapeXml(JSON.stringify(feature.properties));
            const style = feature.properties.feature_type === "central_path"
                ? "#path"
                : feature.properties.feature_type === "time_marker"
                    ? "#marker"
                    : "#line";
            return `    <Placemark><name>${escapeXml(name)}</name><description>${description}</description><styleUrl>${style}</styleUrl>${geometryKml(feature.geometry, this.precision)}</Placemark>`;
        })
            .join("\n");
        const contents = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXml(scene.event.id)}</name>
    <description>Planning-grade ${escapeXml(scene.event.kind)} solar eclipse geometry generated by @found-in-space/shadowline.</description>
    <Style id="path"><LineStyle><color>ff7d286d</color><width>2</width></LineStyle><PolyStyle><color>557d286d</color></PolyStyle></Style>
    <Style id="line"><LineStyle><color>ff8e2c7c</color><width>2</width></LineStyle></Style>
    <Style id="marker"><IconStyle><color>ff251a21</color><scale>0.7</scale></IconStyle></Style>
${placemarks}
  </Document>
</kml>
`;
        return {
            filename: `${scene.event.id}.kml`,
            mimeType: this.mimeType,
            contents,
        };
    }
}
